'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

interface BrandingState {
  iconUrl: string | null;
  textUrl: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setUrls: (iconUrl: string | null, textUrl: string | null) => Promise<void>;
  applySiteColors: (color1: string, color2: string, color3: string) => void;
}

const BrandingContext = createContext<BrandingState>({
  iconUrl: null,
  textUrl: null,
  loading: true,
  refresh: async () => {},
  setUrls: async () => {},
  applySiteColors: () => {},
});

export const useBranding = () => useContext(BrandingContext);

// ─── LocalStorage keys ───────────────────────────────────────────────────────
const LS_ICON_URL = 'branding_icon_url';
const LS_TEXT_URL = 'branding_text_url';
const LS_THEME = 'branding_theme';
const LS_SITE_COLORS = 'branding_site_colors';

// ─── Color extraction helpers ────────────────────────────────────────────────

/** Parse a hex color string (#rgb or #rrggbb) into [r, g, b] 0-255 */
function hexToRgb(hex: string): [number, number, number] | null {
  const clean = hex.replace('#', '');
  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16);
    const g = parseInt(clean[1] + clean[1], 16);
    const b = parseInt(clean[2] + clean[2], 16);
    return [r, g, b];
  }
  if (clean.length === 6) {
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return [r, g, b];
  }
  return null;
}

/** Convert [r,g,b] to hex string */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
}

/** Lighten or darken a hex color by a factor (-1 to 1) */
function adjustColor(hex: string, factor: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const [r, g, b] = rgb;
  if (factor > 0) {
    return rgbToHex(r + (255 - r) * factor, g + (255 - g) * factor, b + (255 - b) * factor);
  } else {
    const f = 1 + factor;
    return rgbToHex(r * f, g * f, b * f);
  }
}

/** Compute perceived luminance (0-1) */
function luminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0.5;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Extract all unique hex colors from SVG text */
function extractColorsFromSvg(svgText: string): string[] {
  const hexPattern = /#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g;
  const found = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = hexPattern.exec(svgText)) !== null) {
    const hex = match[0].toLowerCase();
    const rgb = hexToRgb(hex);
    if (!rgb) continue;
    const [r, g, b] = rgb;
    const lum = luminance(hex);
    if (lum < 0.02 || lum > 0.97) continue;
    const maxDiff = Math.max(Math.abs(r - g), Math.abs(g - b), Math.abs(r - b));
    if (maxDiff < 20) continue;
    found.add(hex);
  }
  return Array.from(found);
}

/** Pick the most "prominent" color — highest saturation among extracted colors */
function pickPrimaryColor(colors: string[]): string | null {
  if (!colors.length) return null;
  let best = colors[0];
  let bestSat = -1;
  for (const hex of colors) {
    const rgb = hexToRgb(hex);
    if (!rgb) continue;
    const [r, g, b] = rgb.map((v) => v / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const sat = max === 0 ? 0 : (max - min) / max;
    if (sat > bestSat) {
      bestSat = sat;
      best = hex;
    }
  }
  return best;
}

/** Derive a full CSS-variable theme palette from a primary color */
function buildTheme(primary: string): Record<string, string> {
  const lum = luminance(primary);
  const isDark = lum < 0.4;

  const background = isDark
    ? adjustColor(primary, -0.88)
    : adjustColor(primary, -0.82);

  const card = adjustColor(background, 0.06);
  const secondary = adjustColor(background, 0.12);
  const muted = adjustColor(background, 0.18);
  const border = adjustColor(primary, -0.6);
  const accent = adjustColor(primary, 0.3);
  const foreground = adjustColor(primary, 0.82);
  const primaryForeground = luminance(primary) > 0.35 ? adjustColor(background, 0) : foreground;
  const ring = primary;
  const mutedForeground = adjustColor(primary, 0.2);

  return {
    '--primary': primary,
    '--primary-foreground': primaryForeground,
    '--background': background,
    '--foreground': foreground,
    '--secondary': secondary,
    '--secondary-foreground': foreground,
    '--accent': accent,
    '--accent-foreground': adjustColor(background, 0),
    '--muted': muted,
    '--muted-foreground': mutedForeground,
    '--card': card,
    '--card-foreground': foreground,
    '--border': border,
    '--input': secondary,
    '--ring': ring,
  };
}

/** Apply a theme palette to the document :root */
function applyTheme(vars: Record<string, string>) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value);
  }
}

/** Reset theme to original CSS-file values by removing inline overrides */
function resetTheme() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const keys = [
    '--primary', '--primary-foreground', '--background', '--foreground',
    '--secondary', '--secondary-foreground', '--accent', '--accent-foreground',
    '--muted', '--muted-foreground', '--card', '--card-foreground',
    '--border', '--input', '--ring',
  ];
  for (const key of keys) {
    root.style.removeProperty(key);
  }
}

/** Fetch SVG text from a URL (same-origin or CORS-permissive) */
async function fetchSvgText(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    let text = await res.text();
    if (!text.trim().startsWith('<svg') && !text.includes('<svg')) return null;
    return text;
  } catch {
    return null;
  }
}

/** Read cached theme from localStorage and apply it immediately (sync) */
function applyCachedTheme() {
  if (typeof window === 'undefined') return;
  try {
    const cached = localStorage.getItem(LS_THEME);
    if (cached) {
      const vars = JSON.parse(cached) as Record<string, string>;
      applyTheme(vars);
    }
  } catch {
    // ignore
  }
}

/** Save theme to localStorage */
function saveThemeToCache(vars: Record<string, string>) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LS_THEME, JSON.stringify(vars));
  } catch {
    // ignore
  }
}

/** Save branding URLs to localStorage */
function saveBrandingToCache(iconUrl: string | null, textUrl: string | null) {
  if (typeof window === 'undefined') return;
  try {
    if (iconUrl) localStorage.setItem(LS_ICON_URL, iconUrl);
    else localStorage.removeItem(LS_ICON_URL);
    if (textUrl) localStorage.setItem(LS_TEXT_URL, textUrl);
    else localStorage.removeItem(LS_TEXT_URL);
  } catch {
    // ignore
  }
}

/** Read cached branding URLs from localStorage */
function readCachedBranding(): { iconUrl: string | null; textUrl: string | null } {
  if (typeof window === 'undefined') return { iconUrl: null, textUrl: null };
  try {
    return {
      iconUrl: localStorage.getItem(LS_ICON_URL),
      textUrl: localStorage.getItem(LS_TEXT_URL),
    };
  } catch {
    return { iconUrl: null, textUrl: null };
  }
}

/** Apply site accent colors to document root */
function applySiteColorsToDoc(color1: string, color2: string, color3: string) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--site-color-1', color1);
  root.style.setProperty('--site-color-2', color2);
  root.style.setProperty('--site-color-3', color3);
  // Mirror site-color-1 into --primary and --ring so all text-primary usages
  // across the site and admin dashboards automatically reflect the branding color
  root.style.setProperty('--primary', color1);
  root.style.setProperty('--ring', color1);
  // Derive a lighter accent from color1 for --accent
  const lum = luminance(color1);
  const accentColor = adjustColor(color1, lum < 0.5 ? 0.35 : -0.2);
  root.style.setProperty('--accent', accentColor);
}

/** Save site colors to localStorage */
function saveSiteColorsToCache(color1: string, color2: string, color3: string) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LS_SITE_COLORS, JSON.stringify({ c1: color1, c2: color2, c3: color3 }));
  } catch { /* ignore */ }
}

/** Apply cached site colors from localStorage */
function applyCachedSiteColors() {
  if (typeof window === 'undefined') return;
  try {
    const cached = localStorage.getItem(LS_SITE_COLORS);
    if (cached) {
      const { c1, c2, c3 } = JSON.parse(cached);
      if (c1) {
        document.documentElement.style.setProperty('--site-color-1', c1);
        // Restore --primary and --ring so text-primary reflects the branding color
        document.documentElement.style.setProperty('--primary', c1);
        document.documentElement.style.setProperty('--ring', c1);
      }
      if (c2) document.documentElement.style.setProperty('--site-color-2', c2);
      if (c3) document.documentElement.style.setProperty('--site-color-3', c3);
    }
  } catch { /* ignore */ }
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  // Always start with null/true to match SSR — hydrate from localStorage in useEffect
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [textUrl, setTextUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  // Apply cached theme synchronously on first mount (before async fetch)
  useEffect(() => {
    applyCachedTheme();
    applyCachedSiteColors();
    // Hydrate branding URLs from localStorage immediately on mount
    const cached = readCachedBranding();
    if (cached.iconUrl) setIconUrl(cached.iconUrl);
    if (cached.textUrl) setTextUrl(cached.textUrl);
  }, []);

  /** Extract colors from icon SVG and apply theme */
  const applyIconTheme = useCallback(async (url: string | null) => {
    if (!url) {
      resetTheme();
      if (typeof window !== 'undefined') {
        try { localStorage.removeItem(LS_THEME); } catch { /* ignore */ }
      }
      return;
    }
    const svgText = await fetchSvgText(url);
    if (!svgText) return;
    const colors = extractColorsFromSvg(svgText);
    const primary = pickPrimaryColor(colors);
    if (!primary) return;
    const theme = buildTheme(primary);
    applyTheme(theme);
    saveThemeToCache(theme);
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', ['logo_icon_url', 'logo_text_url', 'site_color_1', 'site_color_2', 'site_color_3']);
      let icon: string | null = null;
      let text: string | null = null;
      let c1: string | null = null;
      let c2: string | null = null;
      let c3: string | null = null;
      data?.forEach((row: { key: string; value: string | null }) => {
        if (row.key === 'logo_icon_url') icon = row.value;
        if (row.key === 'logo_text_url') text = row.value;
        if (row.key === 'site_color_1') c1 = row.value;
        if (row.key === 'site_color_2') c2 = row.value;
        if (row.key === 'site_color_3') c3 = row.value;
      });
      setIconUrl(icon);
      setTextUrl(text);
      saveBrandingToCache(icon, text);
      await applyIconTheme(icon);
      // Apply site colors if set
      if (c1 || c2 || c3) {
        const col1 = c1 || '#6633ff';
        const col2 = c2 || '#590ce4';
        const col3 = c3 || '#6633cc';
        applySiteColorsToDoc(col1, col2, col3);
        saveSiteColorsToCache(col1, col2, col3);
      }
    } catch {
      // keep existing values on error
    } finally {
      setLoading(false);
    }
  }, [applyIconTheme]);

  // Optimistic setter — called by LogoSettings immediately after upload
  const setUrls = useCallback(
    async (newIcon: string | null, newText: string | null) => {
      setIconUrl(newIcon);
      setTextUrl(newText);
      saveBrandingToCache(newIcon, newText);
      await applyIconTheme(newIcon);
    },
    [applyIconTheme]
  );

  /** Called by ColorSettings after saving — applies and caches the new colors */
  const applySiteColors = useCallback((color1: string, color2: string, color3: string) => {
    applySiteColorsToDoc(color1, color2, color3);
    saveSiteColorsToCache(color1, color2, color3);
  }, []);

  useEffect(() => {
    fetchBranding();

    // Realtime subscription: update whenever app_settings rows change
    const channel = supabase
      .channel('branding-settings')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'app_settings',
          filter: 'key=in.(logo_icon_url,logo_text_url,site_color_1,site_color_2,site_color_3)',
        },
        async (payload) => {
          const { key, value } = payload.new as { key: string; value: string | null };
          if (key === 'logo_icon_url') {
            setIconUrl(value);
            saveBrandingToCache(value, textUrl);
            await applyIconTheme(value);
          }
          if (key === 'logo_text_url') {
            setTextUrl(value);
            saveBrandingToCache(iconUrl, value);
          }
          if (key === 'site_color_1' || key === 'site_color_2' || key === 'site_color_3') {
            // Re-fetch all colors to apply together
            fetchBranding();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchBranding, applyIconTheme]);

  return (
    <BrandingContext.Provider value={{ iconUrl, textUrl, loading, refresh: fetchBranding, setUrls, applySiteColors }}>
      {children}
    </BrandingContext.Provider>
  );
}
